import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { IAddress, ICompanyInfo } from "../services/types/LeadRequisitionType";
import { Checkbox, Input } from "@citi-icg-172888/icgds-react";
import DropdownHoc from "./DropdownHoc";
import cx from "classnames";

interface AddressProps {
  title: string;
  className?: string;
  labelCheckbox?: string;
  data: IAddress | null;
  companyInfo?: ICompanyInfo | null;
  onChange?: (data: IAddress, field: string) => void;
  countries: string[];
  errorStreet: string;
  errorState: string;
  errorCity: string;
  errorZip: string;
}

const Address: React.FC<AddressProps> = ({
  title,
  labelCheckbox,
  data,
  className,
  companyInfo,
  onChange,
  countries,
  errorCity,
  errorState,
  errorZip,
  errorStreet,
}) => {
  const [isAddressSame, setIsAddressSame] = useState<boolean>(false);
  const [currAddress, setCurrAddress] = useState<IAddress | null>(data);
  useEffect(() => {
    if (labelCheckbox) {
      setIsAddressSame(true);
    }
  }, [labelCheckbox]);

  const onClickHandler = useCallback(() => {
    setIsAddressSame(!isAddressSame);
  }, [isAddressSame, setIsAddressSame]);

  const onChangeDdHandler = (name: string, selectedParam: string) => {
    const updatedAddress: IAddress = {
      ...currAddress,
      [name]: selectedParam,
    };
    setCurrAddress(updatedAddress);
    onChange && onChange(updatedAddress, name);
  };

  const onChangeHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      const updatedAddress: IAddress = {
        ...currAddress,
        [name]: value,
      };
      setCurrAddress(updatedAddress);
      onChange && onChange(updatedAddress, name);
    },
    [currAddress, onChange]
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const pattern = /^[A-Za-z\s]*$/;

    if (pattern.test(value)) {
      onChangeHandler(event);
    }
  };

  return (
    <div className={cx(className, " lmn-dnca-w-l-60 lmn-dnca-w-m-60 lmn-dnca-w-xl-60")}>
      <h4 className="lmn-text-color" role="heading" aria-level={4}>
        {title}
      </h4>
      {labelCheckbox && (
        <div className="lmn-mt-5  lmn-text-color-0">
          <Checkbox checked={isAddressSame} size="sm" onClick={onClickHandler}>
            {labelCheckbox}
          </Checkbox>
        </div>
      )}
      {!isAddressSame && (
        <div>
          <div className="">
            <div className="lmn-form-group lmn-mb-5">
              <label className="lmn-required" htmlFor="streetName">Street Name</label>
              <Input
                id="streetName"
                name="streetName"
                value={currAddress?.streetName}
                aria-label="Street Name"
                invalid={!!errorStreet}
                style={{
                  // Subtract 20px from the total width

                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                aria-describedby="street_name"
                aria-required={true}
                onChange={onChangeHandler}
              />
              {errorStreet && <div aria-live="assertive" id="street_name" role="alert">
                <span className="visually-hidden">Street Name - </span>
                <small>{errorStreet}</small></div>}
            </div>
            <div id="selected_country" className="lmn-form-group lmn-mb-5">
              <label className="lmn-required">Country</label>
              <DropdownHoc
                name="country"
                style={{
                  // Subtract 20px from the total width
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                disabled={companyInfo?.global_ultimate_country_name ? true : false}
                options={countries}
                value={companyInfo?.global_ultimate_country_name}
                onChange={(e) => onChangeDdHandler("country", e)}
                iconProps={{ "aria-hidden": "true" }} // Added aria-hidden="true" to the icon
              />
            </div>
          </div>
          <div className=" lmn-dcna-xl-flex lmn-dcna-l-flex lmn-d-md-flex">
            <div className="lmn-dnca-w-l-half lmn-dnca-w-m-half lmn-dnca-w-xl-half lmn-form-group lmn-mb-5">
              <label className="lmn-required" htmlFor="city">City</label>
              <Input
                id="city"
                name="city"
                aria-label="City"
                value={currAddress?.city}
                invalid={!!errorCity}
                
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                pattern="^[A-Za-z\s]+$"
                onChange={handleChange}
                aria-required={true}
                aria-describedby="city_name"
              />
              {errorCity && <div aria-live="assertive" id="city_name" role="alert"> <small>{errorCity}</small> </div> }
            </div>
            <div className="lmn-dnca-w-l-half lmn-dnca-w-m-half lmn-dnca-w-xl-half lmn-flex-grow-1 lmn-form-group lmn-mb-5 lmn-ml-2xl-6 lmn-ml-lg-6 lmn-ml-md-6 lmn-ml-xl-6">
              <label className="lmn-required" htmlFor="state">State / Province</label>
              <Input
                id="state"
                name="state"
                aria-label="State / Province"
                aria-describedby="state_name"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                invalid={!!errorState}
                value={currAddress?.state}
                pattern="^[A-Za-z\s]+$"
                onChange={handleChange}
                aria-required={true}
              />
              {errorState && <div aria-live="assertive" id="state_name" role="alert"> 
                <span className="visually-hidden">State / Province - </span>
                <small>{errorState}</small> </div> }
            </div>
          </div>
          <div className=" lmn-dcna-xl-flex lmn-dcna-l-flex  lmn-mr-lg-6 lmn-mr-xl-6 lmn-mr-2xl-6">
            <div className="lmn-dnca-w-l lmn-dnca-w-l-half lmn-dnca-w-m-half lmn-dnca-w-xl-half lmn-flex-grow-1 lmn-form-group lmn-mb-5">
              <label className="lmn-required" htmlFor="zipcode">Zip/Postal Code</label>
              <Input
                id="zipcode"
                name="zipcode"
                aria-describedby="zip_code"
                aria-label="Zip/Postal Code"
                style={{
                  overflow: "hidden",

                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                value={currAddress?.zipcode}
                onChange={onChangeHandler}
                invalid={!!errorZip}
                aria-required={true}
              />
              {errorZip && <div aria-live="assertive" id="zip_code" role="alert"> <small>{errorZip}</small> </div>}
            </div>
            <div className="lmn-dnca-w-l lmn-dnca-w-l-half lmn-dnca-w-m-half lmn-dnca-w-xl-half lmn-flex-grow-1 lmn-form-group lmn-mb-5"></div>
          </div>
        </div>
      )}
    </div>
  );
};

Address.defaultProps = {
  labelCheckbox: "",
};

export default Address;
